import React, { useEffect } from 'react'
import PaymentTypeSelection from './PaymentTypeSelection'
import { usePaymentStore } from 'stores/paymentStore'
import PageTracker from '../PageTracker/PageTracker'
import PaymentPlan from '../PaymentPlan/PaymentPlan'
import { useSnlInvoiceStore } from 'stores/SNLInvoiceStore'
import SubmitPayment from '../Payments/SubmitPayment'
import { BuilderContent } from '@builder.io/react'
import { PagePropsAbstract } from 'components/pageNavigator'
import UnderstandingYourCosts from '../UnderstandingCostsBlock/UnderstandingYourCosts'
import EditInvoice from '../EditInvoice/EditInvoice'
import PayLaterBlock from '../Payments/PayLaterBlock'

interface PaymentInformationProps {
  paymentAuthorizationNoticeContent: BuilderContent
  onSubmit?: () => void
  displaySubmitError?: boolean
}

const PaymentInformation: React.FC<PaymentInformationProps & PagePropsAbstract> = ({
  paymentAuthorizationNoticeContent,
  onSubmit,
  displaySubmitError,
  ...pageProps
}) => {
  const { resetPaymentStore, payLater } = usePaymentStore()
  const { snlInvoice, editedInvoice, error } = useSnlInvoiceStore()

  useEffect( () => {
    // Reset payment store when component mounts
    resetPaymentStore()
  }, [] )

  const invoice = editedInvoice ?? snlInvoice

  return (
    <div className="flex flex-col items-center w-full mb-8">
      <PageTracker
        title={`Payment Summary`}
        description={<div className="font-light">
          <p className="font-light">
            <span className="font-semibold">
              {`Your payment amounts displayed are the estimated costs`}
            </span>
            {` for your order based on how your plan usually covers supplies.`}
          </p>
          <br />
          <p className="font-light">{`Upon placing your order, our team will verify your insurance and obtain all necessary paperwork from your doctor per your insurance guidelines.`}</p>
        </div>}
      />


      {!error && invoice && (
        <div className="flex flex-col gap-4 w-full mt-4">
          <EditInvoice />
          <UnderstandingYourCosts />
        </div>
      )}

      {
        invoice?.pay_later_eligible_flag && payLater === null && invoice?.total_patient_owes ? (
          <PayLaterBlock />
        ) : (
          <>
            <PaymentPlan total={invoice?.total_patient_owes ?? 0} />
            { ( invoice?.total_patient_owes ?? 0 ) > 0 && <PaymentTypeSelection /> }
            <SubmitPayment paymentAuthorizationNoticeContent={paymentAuthorizationNoticeContent} {...pageProps} />
            {displaySubmitError && (
              <div className="mt-4 p-3 bg-red-100 text-red-800 rounded-md">
                {`There was an error processing your payment information. Please try again.`}
              </div>
            )}
          </>
        )
      }

      {onSubmit && (
        <button
          id="submit-payment"
          className="mt-8 bg-primary text-white py-3 px-8 rounded-md font-medium"
          onClick={onSubmit}
          data-testid="snl-payment-continue-button"
        >
          {`Continue`}
        </button>

      )}
    </div>
  )
}

export default PaymentInformation